import Route from '@ember/routing/route';
import { service } from '@ember/service';
import {
  isPslfDebugActive,
  getPslfDebugState,
  buildPslfDataOverrides,
  buildMockLoanSummary,
} from 'tio-employee/utils/pslf/debug-scenarios';
import type { PslfDebugState } from 'tio-employee/utils/pslf/debug-scenarios';
import type PersonModel from 'tio-common/models/person';
import type PslfProfileModel from 'tio-common/models/pslf-profile';
import type PslfGroup from 'tio-common/models/pslf-group';
import type ObservableDocumentModel from 'tio-common/models/observable-document';
import type { NsldsStructuredContent } from 'tio-common/models/observable-document';
import type RecommendationModel from 'tio-common/models/recommendation';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type SessionContextService from 'tio-employee/services/session-context';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { LivePslfData } from 'tio-employee/services/reimagined-data';

export interface PslfDebugOverrides {
  linkLoans: boolean;
}

export default class AuthenticatedPslfDashboardIndexRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare reimaginedData: ReimaginedDataService;

  queryParams = {
    v2: { refreshModel: true },
  };

  async model(params: { v2?: string }) {
    const isV2 = params.v2 === 'true';
    const userId = this.sessionContext.user.id;
    const personId = this.sessionContext.user.person.id;
    const parentModel = this.modelFor('authenticated.pslf') as {
      pslfProfile: PslfProfileModel;
      groups: PslfGroup[];
    };

    const [loanSummary, person, mostRecentRecQuery] = await Promise.all([
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      this.store.adapterFor('user').getLoanSummary(userId),
      // @ts-expect-error type models
      this.store.findRecord<PersonModel>('person', personId, {
        include: 'accounts.financial-institution,accounts.loans',
        reload: true,
      }),
      this.store.query<RecommendationModel>('recommendation', {
        filter: {
          user: userId,
          'requesting-app': 'PSLF',
        },
        include: 'user',
        sort: '-created-at',
        page: {
          offset: 0,
          limit: 1,
        },
      }),
    ]);

    // Base model (always returned)
    const baseModel = {
      accounts: person.accounts,
      loanSummary: loanSummary,
      mostRecentRecommendation: mostRecentRecQuery?.[0] as RecommendationModel | undefined,
      person: person,
      pslfProfile: parentModel.pslfProfile,
      isV2,
      // v2-only fields (populated below when isV2 is true)
      pslfData: undefined as LivePslfData | undefined,
      nsldsFileRequestDate: null as string | null,
      debugOverrides: undefined as PslfDebugOverrides | undefined,
    };

    if (!isV2) {
      return baseModel;
    }

    // v2 mode: fetch additional data
    const [pslfData, nsldsDocuments] = await Promise.all([
      this.reimaginedData.fetchPslfData(),
      this.store.query<ObservableDocumentModel>('observable-document', {
        filter: {
          person: personId,
          provider: 'ObservableProvider.NSLDS',
        },
        sort: '-created-at',
        page: { limit: 1 },
      }),
    ]);

    // Extract file_request_date from the most recent NSLDS document
    const mostRecentNslds = nsldsDocuments?.[0];
    if (mostRecentNslds) {
      const content = mostRecentNslds.structuredContent as NsldsStructuredContent | undefined;
      baseModel.nsldsFileRequestDate = content?.file?.file_request_date ?? null;
    }

    baseModel.pslfData = pslfData;

    // Apply PSLF debug overrides when ?pslf_debug=true
    if (isPslfDebugActive()) {
      const debugState = getPslfDebugState();

      baseModel.pslfData = buildPslfDataOverrides(debugState, pslfData, this.store);
      baseModel.loanSummary = buildMockLoanSummary(debugState);

      if (debugState.estimatorCompleted || debugState.recertifyIncome) {
        baseModel.mostRecentRecommendation = {
          estimatedMonthlyPayment: 285,
          estimatedForgivenessAmount: 62612.86,
          estimatedForgivenessDate: 2035,
          hasErrorsInCalcs: false,
          formattedBestPlanSummary: {
            payoffAmount: [3078000],
            termMonths: [108],
            monthlyPayments: [28500],
            totalInterestPaid: [1578000],
            remLoanBalance: [6261286],
          },
          formattedCurrentPlanSummary: {
            payoffAmount: [10500000],
            termMonths: [300],
            monthlyPayments: [35000],
            totalInterestPaid: [4250000],
            remLoanBalance: [0],
          },
        } as unknown as RecommendationModel;
      } else {
        baseModel.mostRecentRecommendation = undefined;
      }

      if (debugState.hasNsldsData || debugState.annualReminder) {
        baseModel.nsldsFileRequestDate = '2025-01-15';
      }

      if (debugState.hasNsldsData || debugState.annualReminder) {
        this.pushDebugAccountsIntoStore(person, debugState);
      }

      baseModel.debugOverrides = {
        linkLoans: debugState.linkLoans,
      };
    }

    return baseModel;
  }

  /**
   * Push mock Account and Loan records into the Ember Data store so that
   * person.accounts returns debug data. Unloads existing accounts first
   * to avoid mixing real and mock data.
   */
  private pushDebugAccountsIntoStore(person: PersonModel, debugState: PslfDebugState) {
    const accountId = `debug-nslds-${person.id}`;

    // Unload existing accounts to avoid mixing live and debug data
    const existingAccounts = person.accounts?.slice() ?? [];
    for (const account of existingAccounts) {
      for (const loan of account.loans?.slice() ?? []) {
        loan.unloadRecord();
      }
      account.unloadRecord();
    }

    // Build JSON:API loan resources
    const loanResources = debugState.debugLoans.map((debugLoan, i) => ({
      id: `debug-loan-${i + 1}`,
      type: 'loan',
      attributes: {
        isActive: true,
        observation: {
          'loan-name': debugLoan.name,
          balance: debugLoan.currentBalance,
          'principal-balance': debugLoan.currentBalance,
          'interest-rate': 5.5,
          'forgiveness-program-qualified': debugLoan.qualifyingPayments,
          'is-paid-off': false,
          'loan-type': debugLoan.loanType,
          'repayment-plan-derived': 'ibr',
          status: 'active',
        },
      },
      relationships: {
        account: { data: { type: 'account', id: accountId } },
      },
    }));

    // Push account + loans into the store
    this.store.push({
      data: {
        id: accountId,
        type: 'account',
        attributes: {
          isActive: true,
          observation: {
            provider: 'ObservableProvider.NSLDS',
            balance: debugState.debugLoans.reduce((sum, l) => sum + l.currentBalance, 0),
          },
        },
        relationships: {
          person: { data: { type: 'person', id: person.id } },
          loans: {
            data: loanResources.map((l) => ({ type: 'loan', id: l.id })),
          },
        },
      },
      included: loanResources,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }
}
